import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ACHIEVEMENTS, DIFFICULTIES } from "@/game/config";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export const AchievementsModal = ({ open, onClose, unlocked, playerName }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="pixel-dialog max-w-3xl" data-testid="achievements-modal">
      <DialogHeader>
        <DialogTitle className="dialog-title">Achievements</DialogTitle>
        <DialogDescription className="dialog-sub" data-testid="achievements-progress">
          {playerName || "Hero"} · {unlocked.length}/{ACHIEVEMENTS.length} unlocked
        </DialogDescription>
      </DialogHeader>
      <div className="ach-grid">
        {ACHIEVEMENTS.map((a) => {
          const Icon = Icons[a.icon] || Icons.Award;
          const got = unlocked.includes(a.id);
          return (
            <div key={a.id} className={`ach-card ${got ? "got" : ""}`} data-testid={`achievement-card-${a.id}`} data-unlocked={got}>
              <Icon size={22} />
              <div><strong>{a.name}</strong><small>{a.desc}</small></div>
            </div>
          );
        })}
      </div>
    </DialogContent>
  </Dialog>
);

const AllTime = ({ rows }) => (
  <div className="lb-table">
    <div className="lb-row head"><span>#</span><span>Hero</span><span>Score</span><span>Level</span><span>Mode</span></div>
    {rows.map((r, i) => (
      <div key={r.id} className={`lb-row ${r.won ? "won" : ""}`} data-testid={`leaderboard-row-${i}`}>
        <span>{i + 1}</span><span>{r.name}{r.won && " ★"}</span><span>{r.score.toLocaleString()}</span>
        <span>{r.level_reached}</span><span>{DIFFICULTIES[r.difficulty]?.label}</span>
      </div>
    ))}
  </div>
);

const Daily = ({ rows }) => (
  <div className="lb-table">
    <div className="lb-row head"><span>#</span><span>Hero</span><span>Score</span><span>Time</span><span>Result</span></div>
    {rows.map((r, i) => (
      <div key={r.name} className={`lb-row ${r.completed ? "won" : ""}`} data-testid={`daily-row-${i}`}>
        <span>{i + 1}</span><span>{r.name}</span><span>{r.score.toLocaleString()}</span>
        <span>{Math.floor(r.time / 60)}:{String(r.time % 60).padStart(2, "0")}</span><span>{r.completed ? "Cleared" : "Fell"}</span>
      </div>
    ))}
  </div>
);

export const LeaderboardModal = ({ open, onClose, initialTab = "all" }) => {
  const [rows, setRows] = useState(null);
  const [daily, setDaily] = useState(null);
  useEffect(() => {
    if (!open) return;
    setRows(null); setDaily(null);
    api.leaderboard().then(setRows).catch(() => setRows([]));
    api.dailyLeaderboard().then(setDaily).catch(() => setDaily({ rows: [] }));
  }, [open]);
  const empty = (msg, id) => <p className="dialog-sub" data-testid={id}>{msg}</p>;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="pixel-dialog max-w-3xl" data-testid="leaderboard-modal">
        <DialogHeader>
          <DialogTitle className="dialog-title">Leaderboard</DialogTitle>
          <DialogDescription className="dialog-sub">Top 20 heroes</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue={initialTab}>
          <TabsList className="shop-tabs">
            <TabsTrigger value="all" data-testid="lb-tab-all">All-Time</TabsTrigger>
            <TabsTrigger value="daily" data-testid="lb-tab-daily">Daily {daily?.date || ""}</TabsTrigger>
          </TabsList>
          <TabsContent value="all">
            {!rows ? empty("Loading...") : rows.length ? <AllTime rows={rows} /> : empty("No runs yet. Be the first!", "leaderboard-empty")}
          </TabsContent>
          <TabsContent value="daily">
            {!daily ? empty("Loading...") : daily.rows.length ? <Daily rows={daily.rows} /> : empty("No one has tried today's challenge yet.", "daily-empty")}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
